import {
  Component,
  OnInit,
  ViewEncapsulation,
  Input,
  ViewChild,
  ViewContainerRef,
} from "@angular/core";
import { ChartOptions, ChartType, ChartDataSets } from "chart.js";
import { Label, Color, BaseChartDirective } from "ng2-charts";

import { MultiChartDatasource } from "@/shared-module/models";
import { DialogService } from "@/shared-module/services";

@Component({
  selector: "app-decision-multichart",
  templateUrl: "decisionmultichart.component.html",
  encapsulation: ViewEncapsulation.None,
})
export class DecisionMultiChartComponent implements OnInit {
  constructor(
    private viewContainerRef: ViewContainerRef,
    private dialogService: DialogService,
  ) {}
  data: MultiChartDatasource;
  @Input("data") set setdata(_data: MultiChartDatasource) {
    this.data = _data;
    this.refresh();
  }
  @ViewChild("template", { static: true }) template;
  @ViewChild(BaseChartDirective) chart: BaseChartDirective;
  @Input() hidden: boolean;
  @Input() isWhite: boolean;

  public barChartLabels: Label[] = [];
  public barChartType: ChartType = "bar";
  public barChartLegend = true;
  public barChartData: ChartDataSets[] = [];
  public lineChartColors: Color[] = [];

  public barChartOptions: ChartOptions = {
    aspectRatio: 1.5,
    tooltips: {
      callbacks: {
        title: (items, data) => {
          return this.data.xLabels[items[0].index];
        },
        label: (tooltipItem, data) => {
          let label = data.datasets[tooltipItem.datasetIndex].label || "";

          if (label) {
            label += ": ";
          }
          label +=
            this.data.series[tooltipItem.datasetIndex].yValues[
              tooltipItem.index
            ].yLabel;
          return label;
        },
      },
    },
    responsive: true,
    scales: {
      xAxes: [
        {
          id: "x-axis-0",
          offset: true,
          ticks: {
            fontColor: "white",
          },
          gridLines: {
            drawBorder: false,
            zeroLineColor: "transparent",
            display: false,
          },
        },
      ],
      yAxes: [
        {
          id: "y-axis-0",
          ticks: {
            fontColor: "white",
            beginAtZero: true,
            callback: (value: number) => {
              if (this.getRoundedValue(value) === value || value > 0) {
                return value;
              }
              return "";
            },
          },
          gridLines: {
            drawBorder: true,
            display: true,
            borderDash: [2, 5],
          },
        },
        {
          id: "y-axis-1",
          position: "right",
          ticks: {
            beginAtZero: true,
            max: 100,
            min: 0,
            callback: (value: number) => {
              if (value % 10 === 0 || value > 0) {
                return value + "%";
              }
              return "";
            },
          },
          gridLines: {
            drawBorder: false,
            display: false,
          },
        },
      ],
    },
  };

  zoom() {
    this.dialogService.setZoomInfo(this.data.zoomInfo);
  }

  getRoundedValue(val: number) {
    let roundedValue = 0;
    let sign = 1;
    let multipleOf10 = 1;
    if (val < 0) {
      sign = -1;
      val = Math.abs(val);
    }
    if (val > 0) {
      if (val < 0.1) {
        multipleOf10 = -2;
      } else if (val < 1) {
        multipleOf10 = -1;
      } else if (val < 10) {
        multipleOf10 = 0;
      } else if (val < 100) {
        multipleOf10 = 1;
      } else if (val < 1000) {
        multipleOf10 = 2;
      } else if (val < 10000) {
        multipleOf10 = 3;
      } else if (val < 100000) {
        multipleOf10 = 4;
      } else if (val < 1000000) {
        multipleOf10 = 5;
      }
    }

    roundedValue =
      Math.ceil(val / Math.pow(10, multipleOf10)) * Math.pow(10, multipleOf10);
    return roundedValue * sign;
  }

  ngOnInit() {
    this.viewContainerRef.createEmbeddedView(this.template);
    this.refresh();
  }

  refresh() {
    if (this.data) {
      this.barChartData = [];
      this.lineChartColors = [];
      this.barChartLabels = [];
      let data = [];
      let ds = {};
      let color = {};

      this.data.xLabels.forEach((v) => {
        this.barChartLabels.push(v);
      });

      let minBar = 0;
      let maxBar = 0;
      let minLine = 0;
      let maxLine = 0;

      if (this.isWhite) {
        this.barChartOptions.scales.yAxes[0].ticks.fontColor = "black";
        this.barChartOptions.scales.xAxes[0].ticks.fontColor = "black";
      }

      this.data.series.forEach((s) => {
        let min = 0;
        let max = 100;
        data = [];
        s.yValues.forEach((v) => {
          if (v.y < min) {
            min = v.y;
          }
          if (v.y > max) {
            max = v.y;
          }
          data.push(v.y);
        });

        if (s.type === "bar") {
          // tslint:disable-next-line:object-literal-shorthand
          ds = {
            data: data,
            label: s.caption,
            fill: false,
            stack: s.group,
            type: "bar",
            barThickness: 30,
            order: 1,
          };
          if (min < minBar) {
            minBar = min;
          }
          if (max > maxBar) {
            maxBar = max;
          }
        } else {
          if (this.data.secondAxisY) {
            // tslint:disable-next-line:object-literal-shorthand
            ds = {
              data: data,
              label: s.caption,
              fill: false,
              stack: s.group,
              type: "line",
              yAxisID: "y-axis-1",
              order: 0,
            };
            if (min < minLine) {
              minLine = min;
            }
            if (max > maxLine) {
              maxLine = max;
            }
          } else {
            // tslint:disable-next-line:object-literal-shorthand
            ds = {
              data: data,
              label: s.caption,
              fill: false,
              type: "line",
              order: 0,
            };
          }
        }

        color = {
          // grey
          backgroundColor: s.color,
          borderColor: s.color,
          pointBackgroundColor: s.color,
          pointBorderColor: s.color,
          pointHoverBackgroundColor: s.color,
          pointHoverBorderColor: s.color,
          borderWidth: 1,
        };

        this.barChartData.push(ds);
        this.lineChartColors.push(color);

        if (!this.data.secondAxisY) {
          this.barChartOptions.scales.yAxes[1].display = false;
        } else {
          this.barChartOptions.scales.yAxes[1].ticks.min = Math.floor(min);
          this.barChartOptions.scales.yAxes[1].ticks.max = Math.ceil(max);
        }
      });

      if (minBar < 0 || minLine < 0) {
        maxBar = this.getRoundedValue(maxBar);
        minBar = this.getRoundedValue(minBar);
        maxLine = this.getRoundedValue(maxLine);
        minLine = this.getRoundedValue(minLine);

        const barRatio = maxBar / (maxBar - minBar);
        const lineRatio = maxLine / (maxLine - minLine);

        if (this.data.secondAxisY) {
          if (lineRatio > barRatio) {
            this.barChartOptions.scales.yAxes[1].ticks.min =
              (maxLine / maxBar) * minBar;
            this.barChartOptions.scales.yAxes[1].ticks.max = maxLine;

            this.barChartOptions.scales.yAxes[0].ticks.min = minBar;
            this.barChartOptions.scales.yAxes[0].ticks.max = maxBar;
          } else {
            this.barChartOptions.scales.yAxes[1].ticks.min = minLine;
            this.barChartOptions.scales.yAxes[1].ticks.max = maxLine;

            this.barChartOptions.scales.yAxes[0].ticks.min =
              (maxBar / maxLine) * minLine;
            this.barChartOptions.scales.yAxes[0].ticks.max = maxBar;
          }
        } else {
          this.barChartOptions.scales.yAxes[0].ticks.min = minBar;
          this.barChartOptions.scales.yAxes[0].ticks.max = maxBar;
        }
      }

      if (this.chart) {
        this.chart.update();
        this.chart.ngOnDestroy();
        this.chart.chart = this.chart.getChartBuilder(this.chart.ctx);
      }
    }
  }
}
